import { useState } from "react";
import {
  RiCompassDiscoverLine,
  RiFlag2Line,
  RiMapPin2Line,
  RiNotification3Line,
  RiRouteLine,
  RiSteering2Line,
} from "@remixicon/react";
import type { RemixiconComponentType } from "@remixicon/react";
import { useTranslation } from "react-i18next";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { ButtonGroup } from "~/components/ui/button-group";
import { StarRating } from "~/components/ui/star-rating";
import { PriceDisplay } from "~/components/ui/price-display";
import { ProductImageCarousel } from "~/components/products/pdp/ProductImageCarousel";
import { VariantAttributeSelector } from "~/components/products/pdp/VariantAttributeSelector";
import { useCurrencyPreference } from "~/lib/use-currency-preference";
import { useAddToEmbeddedCart, useEmbeddedCartCatalog } from "~/lib/use-embedded-cart";
import type { ProductSelection } from "~/lib/use-embedded-cart-global";
import type { ProductHeroContent } from "~/data/products/types";

interface HeroSectionProps {
  name: string;
  productCode: string;
  content: ProductHeroContent;
}

const FEATURE_HIGHLIGHT_ICONS: Record<ProductHeroContent["featureHighlights"][number]["icon"], RemixiconComponentType> = {
  location: RiMapPin2Line,
  insights: RiSteering2Line,
  route: RiRouteLine,
  geoZones: RiCompassDiscoverLine,
  alerts: RiNotification3Line,
  speed: RiFlag2Line,
};

// Price always comes from the embedded cart, never from ProductPageContent.
export function HeroSection({ name, productCode, content }: HeroSectionProps) {
  const { t } = useTranslation("products");
  const [currency] = useCurrencyPreference();
  const {
    currencySymbol,
    unitPrice,
    attributes,
    subscriptionUnitPrice,
    subscriptionAdjustedUnitPrice,
    subscriptionDiscountThreshold,
  } = useEmbeddedCartCatalog(productCode, currency);

  // Which value is picked per variant attribute; unpicked defaults to the first option.
  // Computed inline rather than seeded via an effect, so there's no moment where an
  // unpicked attribute resolves to `undefined`.
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const resolveSelectedValue = (attribute: NonNullable<typeof attributes>[number]) =>
    selectedValues[attribute.key] ?? attribute.values[0]?.key ?? "";

  const selection: ProductSelection = Object.fromEntries(
    (attributes ?? []).map((attribute) => [attribute.key, resolveSelectedValue(attribute)]),
  );

  const addToCart = useAddToEmbeddedCart(productCode, selection);

  // Badge + rating. Above the image below md, at the top of the pricing column from md.
  const metadata = (
    <div className="flex items-start justify-between gap-4">
      <Badge
        size="lg"
        variant="semi-transparent"
        className="text-bouncie-500 bg-[#F1F1F1]/50 font-bold"
      >
        {t(content.tagline)}
      </Badge>
      {content.rating != null && content.reviewCount != null && (
        <div className="flex flex-col items-end gap-0">
          <StarRating rating={content.rating} compact color="text-bouncie-500" outline />
          <p className="text-text-body-alt text-xs leading-[18px]">
            {content.reviewCount.toLocaleString()} {t("pdp.reviews_suffix")}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <section className="bg-white">
      <div className="wrap flex flex-col gap-10 pt-4 pb-10 md:flex-row md:items-start md:gap-4 md:py-14 lg:gap-12 lg:pb-7 xl:justify-between">
        <div className="pb-2 md:hidden">{metadata}</div>
        <div className="flex-1 xl:w-[652px] xl:flex-none">
          <ProductImageCarousel images={content.images.map((image) => ({ ...image, alt: t(image.alt) }))} />
        </div>

        <div className="flex flex-1 flex-col gap-8 max-md:pt-2 xl:w-[410px] xl:flex-none">
          <div className="flex flex-col gap-6 md:gap-10">
            <div className="hidden md:block">{metadata}</div>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-start gap-3">
                <div className="flex w-full items-start justify-between gap-3">
                  <h1>
                    {content.wordmark ? (
                      // eslint-disable-next-line local/no-raw-img -- wordmark is always an SVG
                      <img src={content.wordmark} alt={`Bouncie ${name}`} className="h-[34px] w-auto lg:h-12" />
                    ) : (
                      <span className="font-display text-text-heading block h-12 text-4xl leading-[1] font-bold">{name}</span>
                    )}
                  </h1>
                  {typeof unitPrice === "number" && (
                    <PriceDisplay
                      value={unitPrice}
                      currencySymbol={currencySymbol ?? ""}
                      currencyCode={currency.toUpperCase()}
                      className="text-text-body"
                      fontSize={48}
                      weightClassName="font-bold"
                    />
                  )}
                </div>
                <p className="text-text-body-secondary lg:text-md text-base leading-[1.5] lg:leading-[1.1]">
                  {t(content.productDescriptor)}
                </p>
              </div>

              {attributes && attributes.length > 0 && (
                <div className="flex flex-col gap-4">
                  {attributes.map((attribute) => (
                    <VariantAttributeSelector
                      key={attribute.key}
                      attribute={attribute}
                      value={resolveSelectedValue(attribute)}
                      onChange={(value) => setSelectedValues((current) => ({ ...current, [attribute.key]: value }))}
                    />
                  ))}
                </div>
              )}

              <ButtonGroup layout="stack" className="w-full">
                <Button
                  data-sid="pdp-hero-add-to-cart"
                  variant="primary"
                  size="default"
                  className="h-11 w-full text-base leading-5 tracking-[0.16px] lg:h-14"
                  onClick={addToCart}
                >
                  {t(content.ctaLabel)}
                </Button>
                <Button variant="link" size="lg" className="h-auto w-full py-0 underline" asChild>
                  <a data-sid="pdp-hero-secondary-cta" href={content.secondaryCtaHref} target="_blank" rel="noreferrer">
                    {t(content.secondaryCtaLabel)}
                  </a>
                </Button>
              </ButtonGroup>
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:gap-6">
            {content.subscription && typeof subscriptionUnitPrice === "number" && (
              <div className="border-border flex flex-col gap-3 rounded-md border-[0.5px] bg-[rgba(242,246,255,0.35)] px-6 py-4 lg:gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-1 flex-col">
                    <p className="text-text-body text-sm leading-5 font-bold tracking-[0.14px] lg:text-base lg:tracking-[0.16px]">
                      {t(content.subscription.headline)}
                    </p>
                    <p className="text-text-body-alt text-xs leading-[18px]">{t(content.subscription.body)}</p>
                  </div>
                  <PriceDisplay
                    value={subscriptionUnitPrice}
                    currencySymbol={currencySymbol ?? ""}
                    currencyCode={currency.toUpperCase()}
                    className="text-text-body"
                    fontSize={32}
                    weightClassName="font-bold"
                  />
                </div>
                {typeof subscriptionAdjustedUnitPrice === "number" && subscriptionDiscountThreshold && currencySymbol && (
                  <p className="text-xs leading-[18px] font-bold text-[#06893f]">
                    {t("pdp.volume_discount_note", {
                      price: `${currencySymbol}${subscriptionAdjustedUnitPrice.toFixed(2)}`,
                      threshold: subscriptionDiscountThreshold,
                    })}
                  </p>
                )}
              </div>
            )}

            {/* Column-first, label-width columns pushed to the edges */}
            <div className="grid grid-flow-col grid-cols-[max-content_max-content] grid-rows-3 justify-between gap-x-3 gap-y-3 px-6">
              {content.featureHighlights.map((item) => {
                const Icon = FEATURE_HIGHLIGHT_ICONS[item.icon];
                return (
                  <div key={item.label} className="text-text-body flex items-center gap-1 text-sm leading-5 whitespace-nowrap lg:text-base lg:leading-[1.5]">
                    <span className="flex shrink-0 items-center justify-center p-1">
                      <Icon className="text-bouncie-500 size-4" />
                    </span>
                    {t(item.label)}
                  </div>
                );
              })}
            </div>

            <div className="border-border rounded-md border-[0.5px] bg-[rgba(255,255,255,0.35)] px-6 py-4">
              <ul className="text-text-body-alt flex flex-col gap-1 text-xs leading-[18px]">
                {content.valueProps.map((key) => (
                  <li key={key} className="ms-[18px] list-disc">
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
